import { notificationManager } from './pushNotificationService'
import type { RestaurantNotification } from './pushNotificationService'
import { ApiService } from './api'
import { formatCurrency } from '@/lib/utils'

const NOTIFICATION_HOUR = 22 // 22:00 (10 PM) daily check-in
const NOTIFICATION_MINUTE = 0
const STORAGE_KEY = 'last_daily_notification_date'

// Array of catchy notification messages
const DAILY_MESSAGES = [
  "Time to check in! See how your restaurant performed today! 🍽️",
  "Your daily update is ready! Check what's cooking in your business! 🔥",
  "Hey there! Time to review today's orders and sales! 📊",
  "Don't miss out! Your restaurant stats are waiting! ⭐",
  "Evening check-in! See how your restaurant is doing today! 🎯",
  "Quick peek time! Check your today's performance! 💼",
  "Your daily digest is ready! See what's happening! 📈",
  "Time for your restaurant review! Check the dashboard! 🏆",
  "Daily update alert! Your business needs your attention! 💡",
  "Hey owner! Time to see today's progress! 🚀"
]

export class DailyNotificationScheduler {
  private intervalId: number | null = null

  /**
   * Check if the scheduled time has passed for today
   */
  private isPastScheduledTime(): boolean {
    const now = new Date()
    const hour = now.getHours()
    const minute = now.getMinutes()
    return hour > NOTIFICATION_HOUR || (hour === NOTIFICATION_HOUR && minute >= NOTIFICATION_MINUTE)
  }

  /**
   * Check if we've already shown the notification today
   */
  private hasShownToday(): boolean {
    const lastNotificationDate = localStorage.getItem(STORAGE_KEY)
    return lastNotificationDate === new Date().toDateString()
  }

  /**
   * Get a random catchy message from the array
   */
  private getRandomMessage(): string {
    const randomIndex = Math.floor(Math.random() * DAILY_MESSAGES.length)
    return DAILY_MESSAGES[randomIndex]
  }

  /**
   * Send the daily reminder notification
   */
  private async sendDailyNotification(): Promise<void> {
    try {
      // Check if we've already shown notification today
      if (this.hasShownToday()) {
        console.log('Daily notification already shown today')
        return
      }

      // Try to fetch daily revenue for a richer notification
      let message = this.getRandomMessage()
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        if (user.restaurantId) {
          const [metricsResponse, restaurantResponse] = await Promise.all([
            ApiService.getDashboardMetrics(user.restaurantId),
            ApiService.getRestaurantDetails(user.restaurantId)
          ])
          if (metricsResponse.success && metricsResponse.data) {
            const revenue = metricsResponse.data.dailyRevenue
            const orders = metricsResponse.data.dailyBillsCount
            const currency = restaurantResponse.success ? restaurantResponse.data?.currency : undefined
            message = `${formatCurrency(revenue, currency)} from ${orders} orders today!`
          }
        }
      } catch (apiError) {
        console.warn('Failed to fetch daily metrics, using fallback message:', apiError)
      }

      const notification: RestaurantNotification = {
        type: 'daily_reminder',
        title: '🔔 Daily Check-In Time!',
        message,
        urgency: 'normal',
        data: {
          url: '/dashboard',
          timestamp: Date.now()
        }
      }

      await notificationManager.sendNotification(notification)

      // Mark as shown for today
      localStorage.setItem(STORAGE_KEY, new Date().toDateString())

      console.log('✅ Daily notification sent successfully at', new Date().toLocaleTimeString())
    } catch (error) {
      console.error('Failed to send daily notification:', error)
    }
  }

  /**
   * Try to send notification if conditions are met (past time + not shown today)
   */
  private tryNotify(): void {
    if (this.isPastScheduledTime() && !this.hasShownToday()) {
      this.sendDailyNotification()
    }
  }

  /**
   * Start the daily notification scheduler
   */
  public start(): void {
    // Clear any existing interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
    }

    console.log('🔔 Daily notification scheduler started')
    console.log(`⏰ Scheduled for ${NOTIFICATION_HOUR}:${String(NOTIFICATION_MINUTE).padStart(2, '0')}`)

    // Check immediately on start - covers the case where the user opens
    // the app after the scheduled time (timers don't run when app is backgrounded)
    this.tryNotify()

    // Also check every 30 seconds as fallback for when the app stays open
    this.intervalId = window.setInterval(() => {
      this.tryNotify()
    }, 30 * 1000)

    // Listen for app coming back to foreground (visibilitychange)
    // Mobile browsers suspend timers when backgrounded, so we re-check on resume
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      console.log('🔔 App resumed, checking notification schedule...')
      this.tryNotify()
    }
  }

  /**
   * Stop the daily notification scheduler
   */
  public stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    console.log('🔕 Daily notification scheduler stopped')
  }

  /**
   * Manually trigger a test notification (for testing purposes)
   */
  public async testNotification(): Promise<void> {
    console.log('🧪 Sending test notification...')
    localStorage.removeItem(STORAGE_KEY)
    await this.sendDailyNotification()
  }

  /**
   * Reset the notification state (useful for testing)
   */
  public reset(): void {
    localStorage.removeItem(STORAGE_KEY)
    console.log('🔄 Daily notification state reset')
  }

  /**
   * Get information about next scheduled notification
   */
  public getNextNotificationInfo(): { time: Date; minutesUntil: number } {
    const now = new Date()
    const target = new Date()
    target.setHours(NOTIFICATION_HOUR, NOTIFICATION_MINUTE, 0, 0)

    if (now >= target) {
      target.setDate(target.getDate() + 1)
    }

    const msUntilNext = target.getTime() - now.getTime()
    return {
      time: target,
      minutesUntil: Math.round(msUntilNext / 1000 / 60)
    }
  }
}

// Export singleton instance
export const dailyNotificationScheduler = new DailyNotificationScheduler()
